import React, { useState, useEffect, useMemo } from 'react';
import {
    Drawer, Box, Typography, Button, IconButton, Chip, Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const FilterDrawer = ({ open, onClose, positions, selectedPosition, selectedNetTimeFilter, selectedScanMethodFilter, onApplyFilters, employees, searchQuery, attendanceData }) => {
    const [currentPosition, setCurrentPosition] = useState(selectedPosition);
    const [currentNetTimeFilter, setCurrentNetTimeFilter] = useState(selectedNetTimeFilter || 'All');
    const [currentScanMethodFilter, setCurrentScanMethodFilter] = useState(selectedScanMethodFilter || 'All');

    useEffect(() => {
        if (open) {
            setCurrentPosition(selectedPosition);
            setCurrentNetTimeFilter(selectedNetTimeFilter || 'All');
            setCurrentScanMethodFilter(selectedScanMethodFilter || 'All');
        }
    }, [selectedPosition, selectedNetTimeFilter, selectedScanMethodFilter, open]);

    const calculateNetTime = (attendanceRecords) => {
        if (!attendanceRecords || attendanceRecords.length === 0) return 0;

        const record = attendanceRecords[0];
        if (!record.start_time || !record.end_time) return 0;

        const totalTime = new Date(record.end_time) - new Date(record.start_time);
        const breakTime = record.breaks ?
            record.breaks.reduce((acc, br) =>
                acc + ((br.break_start_time && br.break_end_time) ?
                    (new Date(br.break_end_time) - new Date(br.break_start_time)) : 0), 0) : 0;

        return totalTime - breakTime;
    };

    const filteredCount = useMemo(() => {
        if (!employees) return 0;
        return employees.filter(emp => {
            const matchesPosition = currentPosition === 'All' || emp.position === currentPosition;
            const matchesSearch = !searchQuery ||
                emp.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                (emp.position && emp.position.toLowerCase().includes(searchQuery.toLowerCase())) ||
                emp.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
                (emp.phone_number && emp.phone_number.includes(searchQuery));

            // Net time filter
            const attendanceInfo = attendanceData?.find(att => att.employee_id === emp.id);
            const netTime = calculateNetTime(attendanceInfo?.attendance_records);
            const eightHoursMs = 8 * 60 * 60 * 1000; // 8 hodín v milisekundách

            let matchesNetTime = true;
            if (currentNetTimeFilter === 'Over 8h') {
                matchesNetTime = netTime > eightHoursMs;
            } else if (currentNetTimeFilter === 'Under 8h') {
                matchesNetTime = netTime > 0 && netTime <= eightHoursMs;
            }
            // 'All' matches everything

            // Scan method filter
            let matchesScanMethod = true;
            if (currentScanMethodFilter !== 'All') {
                // Ak nemá žiadne záznamy, nezhoduje sa s žiadnym scan method filtrom
                if (!attendanceInfo?.attendance_records?.length) {
                    matchesScanMethod = false;
                } else {
                    const record = attendanceInfo.attendance_records[0];
                    if (currentScanMethodFilter === 'NFC') {
                        matchesScanMethod = (record.scan_type_start === 'nfc' || record.scan_type_end === 'nfc');
                    } else if (currentScanMethodFilter === 'QR') {
                        matchesScanMethod = (record.scan_type_start === 'qr_code' || record.scan_type_end === 'qr_code');
                    }
                }
            }

            return matchesPosition && matchesSearch && matchesNetTime && matchesScanMethod;
        }).length;
    }, [employees, currentPosition, currentNetTimeFilter, currentScanMethodFilter, searchQuery, attendanceData]);

    const handleApply = () => {
        onApplyFilters(currentPosition, null, null, currentNetTimeFilter, currentScanMethodFilter);
        onClose();
    };

    const handleClear = () => {
        setCurrentPosition('All');
        setCurrentNetTimeFilter('All');
        setCurrentScanMethodFilter('All');
    };

    const netTimeOptions = [
        { value: 'All', label: 'All work times' },
        { value: 'Over 8h', label: 'Over 8 hours' },
        { value: 'Under 8h', label: 'Under 8 hours' }
    ];

    const scanMethodOptions = [
        { value: 'All', label: 'All scan methods' },
        { value: 'NFC', label: 'NFC only' },
        { value: 'QR', label: 'QR only' }
    ];

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            keepMounted={false}
            disableRestoreFocus={true}
            PaperProps={{ sx: { width: { xs: '100%', sm: 380 } } }}
        >
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
                    Filters
                </Typography>
                <IconButton onClick={onClose} aria-label="close">
                    <CloseIcon />
                </IconButton>
            </Box>
            <Divider />
            <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Position
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 4 }}>
                    {positions.map(pos => (
                        <Chip
                            key={pos}
                            label={pos}
                            onClick={() => setCurrentPosition(pos)}
                            sx={{
                                fontWeight: 500,
                                borderRadius: '16px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease-in-out',
                                border: '1px solid transparent',
                                p: '12px 8px',

                                // Inactive state
                                bgcolor: 'rgba(0, 0, 0, 0.05)',
                                color: 'text.primary',
                                '&:hover': {
                                    bgcolor: 'rgba(0, 0, 0, 0.08)',
                                },

                                // Active (selected) state
                                ...(currentPosition === pos && {
                                    bgcolor: '#2563EB',
                                    color: 'white',
                                    '&:hover': {
                                        bgcolor: '#1D4ED8',
                                    },
                                }),
                            }}
                        />
                    ))}
                </Box>

                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Net work time
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 4 }}>
                    {netTimeOptions.map(option => (
                        <Chip
                            key={option.value}
                            label={option.label}
                            onClick={() => setCurrentNetTimeFilter(option.value)}
                            sx={{
                                fontWeight: 500,
                                borderRadius: '16px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease-in-out',
                                border: '1px solid transparent',
                                p: '12px 8px',

                                // Inactive state
                                bgcolor: 'rgba(0, 0, 0, 0.05)',
                                color: 'text.primary',
                                '&:hover': {
                                    bgcolor: 'rgba(0, 0, 0, 0.08)',
                                },

                                // Active (selected) state
                                ...(currentNetTimeFilter === option.value && {
                                    bgcolor: '#2563EB',
                                    color: 'white',
                                    '&:hover': {
                                        bgcolor: '#1D4ED8',
                                    },
                                }),
                            }}
                        />
                    ))}
                </Box>

                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Scan method
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 4 }}>
                    {scanMethodOptions.map(option => (
                        <Chip
                            key={option.value}
                            label={option.label}
                            onClick={() => setCurrentScanMethodFilter(option.value)}
                            sx={{
                                fontWeight: 500,
                                borderRadius: '16px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease-in-out',
                                border: '1px solid transparent',
                                p: '12px 8px',

                                // Inactive state
                                bgcolor: 'rgba(0, 0, 0, 0.05)',
                                color: 'text.primary',
                                '&:hover': {
                                    bgcolor: 'rgba(0, 0, 0, 0.08)',
                                },

                                // Active (selected) state
                                ...(currentScanMethodFilter === option.value && {
                                    bgcolor: '#2563EB',
                                    color: 'white',
                                    '&:hover': {
                                        bgcolor: '#1D4ED8',
                                    },
                                }),
                            }}
                        />
                    ))}
                </Box>
            </Box>
            <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', bgcolor: 'background.paper', boxShadow: '0 -2px 10px rgba(0,0,0,0.05)' }}>
                <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleClear}
                    size="large"
                    sx={{
                        mb: 2,
                        borderColor: 'text.secondary',
                        color: 'text.secondary',
                        '&:hover': {
                            borderColor: 'text.primary',
                            color: 'text.primary',
                        },
                        py: 1.5,
                        fontWeight: 'bold',
                        borderRadius: 3,
                        textTransform: 'none',
                        fontSize: '1rem'
                    }}
                >
                    Clear
                </Button>
                <Button
                    fullWidth
                    variant="contained"
                    onClick={handleApply}
                    size="large"
                    sx={{
                        bgcolor: '#2563EB',
                        '&:hover': { bgcolor: '#1D4ED8' },
                        py: 1.5,
                        fontWeight: 'bold',
                        borderRadius: 3,
                        textTransform: 'none',
                        fontSize: '1rem'
                    }}
                >
                    Show {filteredCount} results
                </Button>
            </Box>
        </Drawer>
    );
};

export default FilterDrawer; 